import type { MapStylePreference } from '@/config/mapStyle'
import { resolveMapStyle } from '@/config/mapStyle'
import { parseLineString } from '@/services/lineStringService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { isTauri, isTauriAndroidMobile } from '@/services/platformService'
import { toNativeTileUrl } from '@/services/routecampTilesUrl'
import type { WorkspacePOI } from '@/types/poi'
import type { Tour } from '@/types/tour'

export interface TileBbox {
  minLon: number
  minLat: number
  maxLon: number
  maxLat: number
}

export interface TileCoord {
  z: number
  x: number
  y: number
}

export type OfflineCoverageCell = TileCoord

export type TileSourceCategory = 'mapData' | 'terrain'

export interface TileSourceInfo {
  sourceId: string
  urlTemplate: string
  ext: string
  minzoom: number
  maxzoom: number
  category: TileSourceCategory
}

export interface OfflineCategoryEstimate {
  tileCount: number
  estimatedBytes: number
}

export interface OfflineEstimate {
  tileCount: number
  estimatedBytes: number
  mapData: OfflineCategoryEstimate
  terrain: OfflineCategoryEstimate
  sources: TileSourceInfo[]
}

export const OFFLINE_MAX_ZOOM = 14
export const OFFLINE_COVERAGE_MAX_ZOOM = 21
export const OFFLINE_SELECTION_ZOOM_OFFSET = 3
export const MANUAL_MAP_PACKAGE_ID = 'manual-map'
export const MANUAL_MAP_DISPLAY_NAME = 'Downloaded map areas'
export const MANUAL_MAP_MAX_COVERAGE_CELLS = 4096
export const MANUAL_MAP_MAX_TILE_JOBS = 100_000
export const MANUAL_MAP_MAX_ESTIMATED_BYTES = 5_000_000_000

const VECTOR_BYTES_PER_TILE = 25_000
const RASTER_BYTES_PER_TILE = 250_000

export class OfflineCoverageLimitError extends Error {
  constructor(message = 'This area is too large. Zoom in and select a smaller area.') {
    super(message)
    this.name = 'OfflineCoverageLimitError'
  }
}

interface TileJson {
  tiles?: string[]
  minzoom?: number
  maxzoom?: number
}

export interface DownloadTileJob {
  z: number
  x: number
  y: number
  url: string
  ext: string
}

function lon2tile(lon: number, zoom: number): number {
  return Math.floor(((lon + 180) / 360) * Math.pow(2, zoom))
}

function lat2tile(lat: number, zoom: number): number {
  return Math.floor(
    ((1 -
      Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) / Math.PI) /
      2) *
      Math.pow(2, zoom),
  )
}

type BboxPoint = Pick<WorkspacePOI, 'lat' | 'lon'>

export function computeWorkspaceBbox(tours: Tour[], pois: BboxPoint[] = []): TileBbox | null {
  let minLon = Infinity
  let maxLon = -Infinity
  let minLat = Infinity
  let maxLat = -Infinity

  const includePoint = (lon: number, lat: number) => {
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return
    if (lon < minLon) minLon = lon
    if (lon > maxLon) maxLon = lon
    if (lat < minLat) minLat = lat
    if (lat > maxLat) maxLat = lat
  }

  for (const tour of tours) {
    for (const track of tour.tracks) {
      const lineString = track.pointsLineReduced500m || track.pointsLineReduced5m
      if (lineString) {
        const coords = parseLineString(lineString)
        for (const [lon, lat] of coords) {
          includePoint(lon, lat)
        }
      } else {
        for (const segment of track.segments) {
          for (const pt of segment.points) {
            includePoint(pt.lon, pt.lat)
          }
        }
      }
    }

    for (const waypoint of tour.waypoints) {
      includePoint(waypoint.lon, waypoint.lat)
    }
  }

  for (const poi of pois) {
    includePoint(poi.lon, poi.lat)
  }

  if (!isFinite(minLon)) return null

  const padLon = (maxLon - minLon) * 0.07 + 0.01
  const padLat = (maxLat - minLat) * 0.07 + 0.01

  return {
    minLon: minLon - padLon,
    maxLon: maxLon + padLon,
    minLat: minLat - padLat,
    maxLat: maxLat + padLat,
  }
}

export function enumerateTiles(bbox: TileBbox, maxZoom: number): TileCoord[] {
  const tiles: TileCoord[] = []

  for (let z = 0; z <= maxZoom; z++) {
    const xMin = lon2tile(bbox.minLon, z)
    const xMax = lon2tile(bbox.maxLon, z)
    const yMin = lat2tile(bbox.maxLat, z)
    const yMax = lat2tile(bbox.minLat, z)

    for (let x = xMin; x <= xMax; x++) {
      for (let y = yMin; y <= yMax; y++) {
        tiles.push({ z, x, y })
      }
    }
  }

  return tiles
}

function coverageCellKey(cell: OfflineCoverageCell): string {
  return `${cell.z}/${cell.x}/${cell.y}`
}

function validateCoverageCell(cell: OfflineCoverageCell): void {
  if (!Number.isInteger(cell.z) || cell.z < 0 || cell.z > OFFLINE_COVERAGE_MAX_ZOOM) {
    throw new Error(`Offline coverage zoom must be between 0 and ${OFFLINE_COVERAGE_MAX_ZOOM}`)
  }

  const coordinateLimit = 2 ** cell.z
  if (
    !Number.isInteger(cell.x) ||
    !Number.isInteger(cell.y) ||
    cell.x < 0 ||
    cell.y < 0 ||
    cell.x >= coordinateLimit ||
    cell.y >= coordinateLimit
  ) {
    throw new Error('Offline coverage cell coordinates are out of range')
  }
}

function parentCoverageCell(cell: OfflineCoverageCell): OfflineCoverageCell | null {
  if (cell.z === 0) return null
  return {
    z: cell.z - 1,
    x: Math.floor(cell.x / 2),
    y: Math.floor(cell.y / 2),
  }
}

export function coverageCellContains(
  parent: OfflineCoverageCell,
  child: OfflineCoverageCell,
): boolean {
  if (parent.z > child.z) return false
  const zoomDelta = child.z - parent.z
  const scale = 2 ** zoomDelta
  return Math.floor(child.x / scale) === parent.x && Math.floor(child.y / scale) === parent.y
}

function coverageCellChildren(cell: OfflineCoverageCell): OfflineCoverageCell[] {
  const z = cell.z + 1
  const x = cell.x * 2
  const y = cell.y * 2
  return [
    { z, x, y },
    { z, x: x + 1, y },
    { z, x, y: y + 1 },
    { z, x: x + 1, y: y + 1 },
  ]
}

/** Keeps coverage as a compact, non-overlapping quadtree cell set. */
export function normalizeCoverageCells(
  coverageCells: OfflineCoverageCell[],
): OfflineCoverageCell[] {
  const normalized = new Map<string, OfflineCoverageCell>()
  const sorted = [...coverageCells].sort((left, right) => left.z - right.z)

  for (const originalCell of sorted) {
    validateCoverageCell(originalCell)
    let cell = { ...originalCell }

    if (
      Array.from(normalized.values()).some((candidate) => coverageCellContains(candidate, cell))
    ) {
      continue
    }

    for (const candidate of normalized.values()) {
      if (coverageCellContains(cell, candidate)) {
        normalized.delete(coverageCellKey(candidate))
      }
    }
    normalized.set(coverageCellKey(cell), cell)

    while (cell.z > 0) {
      const parent = parentCoverageCell(cell)
      if (!parent) break
      const siblings = coverageCellChildren(parent)
      if (!siblings.every((sibling) => normalized.has(coverageCellKey(sibling)))) break

      for (const sibling of siblings) {
        normalized.delete(coverageCellKey(sibling))
      }
      cell = parent
      normalized.set(coverageCellKey(cell), cell)
    }
  }

  const result = Array.from(normalized.values()).sort(
    (left, right) => left.z - right.z || left.x - right.x || left.y - right.y,
  )
  if (result.length > MANUAL_MAP_MAX_COVERAGE_CELLS) {
    throw new OfflineCoverageLimitError('Too many separate areas are selected. Remove some areas.')
  }
  return result
}

export function isCoverageCellCovered(
  coverageCells: OfflineCoverageCell[],
  cell: OfflineCoverageCell,
): boolean {
  validateCoverageCell(cell)
  return normalizeCoverageCells(coverageCells).some((candidate) =>
    coverageCellContains(candidate, cell),
  )
}

export function doesCoverageIntersectCell(
  coverageCells: OfflineCoverageCell[],
  cell: OfflineCoverageCell,
): boolean {
  validateCoverageCell(cell)
  return normalizeCoverageCells(coverageCells).some(
    (candidate) => coverageCellContains(candidate, cell) || coverageCellContains(cell, candidate),
  )
}

export function addCoverageCell(
  coverageCells: OfflineCoverageCell[],
  cell: OfflineCoverageCell,
): OfflineCoverageCell[] {
  return normalizeCoverageCells([...coverageCells, cell])
}

export function removeCoverageCell(
  coverageCells: OfflineCoverageCell[],
  cell: OfflineCoverageCell,
): OfflineCoverageCell[] {
  validateCoverageCell(cell)
  const normalized = normalizeCoverageCells(coverageCells)
  const next = normalized.filter((candidate) => !coverageCellContains(cell, candidate))
  const containingCell = next.find((candidate) => coverageCellContains(candidate, cell))

  if (!containingCell) return next
  next.splice(next.indexOf(containingCell), 1)

  let branch = containingCell
  while (branch.z < cell.z) {
    const children = coverageCellChildren(branch)
    const selectedChild = children.find((child) => coverageCellContains(child, cell))
    if (!selectedChild) break
    next.push(...children.filter((child) => child !== selectedChild))
    branch = selectedChild
  }

  return normalizeCoverageCells(next)
}

export function toggleCoverageCell(
  coverageCells: OfflineCoverageCell[],
  cell: OfflineCoverageCell,
): OfflineCoverageCell[] {
  return isCoverageCellCovered(coverageCells, cell)
    ? removeCoverageCell(coverageCells, cell)
    : addCoverageCell(coverageCells, cell)
}

export function selectionZoomForMapZoom(mapZoom: number): number {
  return Math.max(
    0,
    Math.min(OFFLINE_COVERAGE_MAX_ZOOM, Math.floor(mapZoom) + OFFLINE_SELECTION_ZOOM_OFFSET),
  )
}

export function coverageCellBounds(cell: OfflineCoverageCell): TileBbox {
  validateCoverageCell(cell)
  const scale = 2 ** cell.z
  const tileYToLatitude = (y: number) =>
    (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / scale))) * 180) / Math.PI

  return {
    minLon: (cell.x / scale) * 360 - 180,
    maxLon: ((cell.x + 1) / scale) * 360 - 180,
    minLat: tileYToLatitude(cell.y + 1),
    maxLat: tileYToLatitude(cell.y),
  }
}

export function enumerateCoverageTiles(
  coverageCells: OfflineCoverageCell[],
  maxZoom = OFFLINE_MAX_ZOOM,
  maxTileCount = Number.POSITIVE_INFINITY,
): TileCoord[] {
  const normalized = normalizeCoverageCells(coverageCells)
  const tiles: TileCoord[] = []

  for (let z = 0; z <= maxZoom; z++) {
    const zoomTiles = new Map<string, TileCoord>()

    for (const cell of normalized) {
      if (z <= cell.z) {
        const scale = 2 ** (cell.z - z)
        const tile = { z, x: Math.floor(cell.x / scale), y: Math.floor(cell.y / scale) }
        zoomTiles.set(coverageCellKey(tile), tile)
        continue
      }

      const scale = 2 ** (z - cell.z)
      const xStart = cell.x * scale
      const yStart = cell.y * scale
      for (let x = xStart; x < xStart + scale; x++) {
        for (let y = yStart; y < yStart + scale; y++) {
          const tile = { z, x, y }
          zoomTiles.set(coverageCellKey(tile), tile)
          if (tiles.length + zoomTiles.size > maxTileCount) {
            throw new OfflineCoverageLimitError()
          }
        }
      }
    }

    tiles.push(...zoomTiles.values())
    if (tiles.length > maxTileCount) throw new OfflineCoverageLimitError()
  }

  return tiles
}

export function estimateCoverageSize(
  coverageCells: OfflineCoverageCell[],
  sources: TileSourceInfo[],
  maxZoom = OFFLINE_MAX_ZOOM,
): OfflineEstimate {
  const tiles = enumerateCoverageTiles(coverageCells, maxZoom, MANUAL_MAP_MAX_TILE_JOBS)
  const categories: Record<TileSourceCategory, OfflineCategoryEstimate> = {
    mapData: { tileCount: 0, estimatedBytes: 0 },
    terrain: { tileCount: 0, estimatedBytes: 0 },
  }

  for (const source of sources) {
    const tileCount = tiles.filter(
      (tile) => tile.z >= source.minzoom && tile.z <= source.maxzoom,
    ).length
    const estimatedBytes =
      tileCount * (source.ext === 'pbf' ? VECTOR_BYTES_PER_TILE : RASTER_BYTES_PER_TILE)
    categories[source.category].tileCount += tileCount
    categories[source.category].estimatedBytes += estimatedBytes
  }

  const estimatedBytes = categories.mapData.estimatedBytes + categories.terrain.estimatedBytes
  const tileCount = categories.mapData.tileCount + categories.terrain.tileCount
  return {
    tileCount,
    estimatedBytes,
    mapData: categories.mapData,
    terrain: categories.terrain,
    sources,
  }
}

export function assertManualMapEstimateWithinLimits(estimate: OfflineEstimate): void {
  if (
    estimate.estimatedBytes > MANUAL_MAP_MAX_ESTIMATED_BYTES ||
    estimate.tileCount > MANUAL_MAP_MAX_TILE_JOBS
  ) {
    throw new OfflineCoverageLimitError()
  }
}

function tileUrl(template: string, z: number, x: number, y: number): string {
  return template.replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y))
}

function extFromTemplate(template: string): string {
  const m = template.match(/\.(pbf|mvt|png|jpg|jpeg|webp|avif)(\?|$)/i)
  if (m) return normalizeTileExtension(m[1])
  if (template.includes('pbf') || template.includes('vector')) return 'pbf'
  return 'png'
}

function normalizeTileExtension(ext: string): string {
  const normalized = ext.toLowerCase()
  if (normalized === 'jpeg') return 'jpg'
  if (normalized === 'mvt') return 'pbf'
  return normalized
}

function isSupportedSourceType(type: string | undefined): boolean {
  return type === 'vector' || type === 'raster' || type === 'raster-dem'
}

async function fetchTileJson(url: string): Promise<TileJson> {
  try {
    const requestUrl = isTauri() ? (toNativeTileUrl(url, isTauriAndroidMobile()) ?? url) : url
    const resp = await fetch(requestUrl)
    if (!resp.ok) {
      throw new Error(`TileJSON request failed with HTTP ${resp.status}`)
    }
    const json = (await resp.json()) as TileJson
    return {
      tiles: json.tiles ?? [],
      minzoom: json.minzoom,
      maxzoom: json.maxzoom,
    }
  } catch (error) {
    logService.warn(LOG_CATEGORIES.OFFLINE, 'Failed to fetch TileJSON', { url, error })
    throw error
  }
}

type StyleJson = {
  sources?: Record<
    string,
    {
      type?: string
      tiles?: string[]
      url?: string
      minzoom?: number
      maxzoom?: number
    }
  >
}

export async function discoverTileSources(
  preference: MapStylePreference,
): Promise<TileSourceInfo[]> {
  let styleJson: StyleJson | null = null

  try {
    const resolved = resolveMapStyle(preference)

    if (typeof resolved === 'string') {
      const requestUrl = isTauri()
        ? (toNativeTileUrl(resolved, isTauriAndroidMobile()) ?? resolved)
        : resolved
      const resp = await fetch(requestUrl)
      if (!resp.ok) {
        throw new Error(`Map style request failed with HTTP ${resp.status}`)
      }
      styleJson = (await resp.json()) as StyleJson
    } else if (typeof resolved === 'object' && resolved !== null) {
      styleJson = resolved as unknown as StyleJson
    }
  } catch (error) {
    logService.warn(LOG_CATEGORIES.OFFLINE, 'Failed to resolve map style for tile discovery', {
      preference,
      error,
    })
    throw error
  }

  if (!styleJson?.sources) return []

  const sources: TileSourceInfo[] = []

  for (const [sourceId, source] of Object.entries(styleJson.sources)) {
    if (!isSupportedSourceType(source.type)) continue

    let templates: string[] = []
    let discoveredMinZoom: number | undefined
    let discoveredMaxZoom: number | undefined

    if (source.tiles && source.tiles.length > 0) {
      templates = source.tiles
    } else if (source.url) {
      const tileJson = await fetchTileJson(source.url)
      templates = tileJson.tiles ?? []
      discoveredMinZoom = tileJson.minzoom
      discoveredMaxZoom = tileJson.maxzoom
    }

    if (templates.length === 0) {
      throw new Error(`Map source "${sourceId}" does not provide an offline tile template`)
    }

    const template = templates[0]
    sources.push({
      sourceId,
      urlTemplate: template,
      ext: extFromTemplate(template),
      minzoom: source.minzoom ?? discoveredMinZoom ?? 0,
      maxzoom: source.maxzoom ?? discoveredMaxZoom ?? 16,
      // raster-dem powers hillshade + on-device contour generation; everything
      // else (vector basemap, future satellite raster) counts as map data.
      category: source.type === 'raster-dem' ? 'terrain' : 'mapData',
    })
  }

  return sources
}

export async function estimateOfflineSize(
  tours: Tour[],
  preference: MapStylePreference,
  maxZoom = OFFLINE_MAX_ZOOM,
  pois: BboxPoint[] = [],
): Promise<OfflineEstimate> {
  const bbox = computeWorkspaceBbox(tours, pois)
  if (!bbox) {
    return {
      tileCount: 0,
      estimatedBytes: 0,
      mapData: { tileCount: 0, estimatedBytes: 0 },
      terrain: { tileCount: 0, estimatedBytes: 0 },
      sources: [],
    }
  }

  const sources = await discoverTileSources(preference)
  const tiles = enumerateTiles(bbox, maxZoom)

  const categories: Record<TileSourceCategory, OfflineCategoryEstimate> = {
    mapData: { tileCount: 0, estimatedBytes: 0 },
    terrain: { tileCount: 0, estimatedBytes: 0 },
  }

  for (const src of sources) {
    const sourceTiles = tiles.filter((t) => t.z >= src.minzoom && t.z <= src.maxzoom).length
    const bytesPerTile = src.ext === 'pbf' ? VECTOR_BYTES_PER_TILE : RASTER_BYTES_PER_TILE
    categories[src.category].tileCount += sourceTiles
    categories[src.category].estimatedBytes += sourceTiles * bytesPerTile
  }

  return {
    tileCount: categories.mapData.tileCount + categories.terrain.tileCount,
    estimatedBytes: categories.mapData.estimatedBytes + categories.terrain.estimatedBytes,
    mapData: categories.mapData,
    terrain: categories.terrain,
    sources,
  }
}

export function selectTileSourcesForDownload(
  sources: TileSourceInfo[],
  options: { includeTerrain: boolean },
): TileSourceInfo[] {
  return sources.filter((src) => options.includeTerrain || src.category !== 'terrain')
}

export function buildTileJobs(tiles: TileCoord[], source: TileSourceInfo): DownloadTileJob[] {
  return tiles
    .filter((t) => t.z >= source.minzoom && t.z <= source.maxzoom)
    .map((t) => ({
      z: t.z,
      x: t.x,
      y: t.y,
      url: tileUrl(source.urlTemplate, t.z, t.x, t.y),
      ext: source.ext,
    }))
}
